import { useEffect, useRef, useState } from 'react';
import './FullscreenViewer.scss';

interface FullscreenViewerProps {
  open: boolean;
  onClose: () => void;
  aspectRatio: number;
  headerLeft?: React.ReactNode;
  children: React.ReactNode;
}

export default function FullscreenViewer({ open, onClose, aspectRatio, headerLeft, children }: FullscreenViewerProps) {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setSize(null);
      return;
    }

    const recalc = () => {
      const scene = sceneRef.current;
      if (!scene) return;
      const { width: sceneWidth, height: sceneHeight } = scene.getBoundingClientRect();
      let width = sceneWidth;
      let height = width / aspectRatio;
      if (height > sceneHeight) {
        height = sceneHeight;
        width = height * aspectRatio;
      }
      setSize({ width, height });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', recalc);
    document.body.style.overflow = 'hidden';
    recalc();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', recalc);
      document.body.style.overflow = '';
    };
  }, [open, aspectRatio, onClose]);

  if (!open) return null;

  return (
    <div className="fullscreen-viewer">
      <div className="fullscreen-viewer__header">
        <div className="fullscreen-viewer__header-left">{headerLeft}</div>
        <div className="fullscreen-viewer__header-right">
          <span>Thoát toàn màn hình</span> 
          <button className="fullscreen-viewer__close" onClick={onClose} title="Thoát toàn màn hình">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </div>
      </div>
      <div className="fullscreen-viewer__scene" ref={sceneRef}>
        {size && (
          <div className="fullscreen-viewer__content" style={{ width: size.width, height: size.height }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
